/**
 * Geometry for the draggable Indy launcher, shared by ChatbotLauncher (which
 * moves it) and ChatbotModal (which opens the panel from wherever it was
 * parked). Kept out of both components so the two can never disagree about
 * where the button is — the panel's opening corner is derived from the same
 * numbers the button renders at.
 *
 * Coordinates are viewport pixels of the button's TOP-LEFT corner, matching the
 * `left`/`top` the component writes. The CSS default is expressed as
 * right/bottom insets, so `defaultLauncherPosition` mirrors the values in
 * ChatbotLauncher.css — including the 16px inset below 640px. Keep them in
 * step; there is no way to read a CSS inset back off an element that has been
 * moved to `left`/`top`.
 */

import type { LauncherPosition } from "~/atoms/chatbot";

export type { LauncherPosition };

/** Matches the disc's width/height in ChatbotLauncher.css. */
export const LAUNCHER_SIZE = 56;

/** The `right`/`bottom` inset in ChatbotLauncher.css, and its ≤640px override. */
const MARGIN = 24;
const MARGIN_SMALL = 16;
const SMALL_SCREEN_MAX = 640;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function margin(): number {
  return window.innerWidth <= SMALL_SCREEN_MAX ? MARGIN_SMALL : MARGIN;
}

/** Where the launcher rests when the user has never moved it. */
export function defaultLauncherPosition(): LauncherPosition {
  const m = margin();
  return {
    x: window.innerWidth - LAUNCHER_SIZE - m,
    y: window.innerHeight - LAUNCHER_SIZE - m,
  };
}

/**
 * Pull a position back inside the viewport. Applied both while dragging and on
 * read, so a spot saved on a wide monitor cannot strand the button off-screen
 * on a laptop — the one failure mode that would leave Indy unopenable.
 */
export function clampLauncherPosition(pos: LauncherPosition): LauncherPosition {
  return {
    x: clamp(pos.x, 0, Math.max(0, window.innerWidth - LAUNCHER_SIZE)),
    y: clamp(pos.y, 0, Math.max(0, window.innerHeight - LAUNCHER_SIZE)),
  };
}

/** The launcher's current top-left: the saved spot if it has one, else the default. */
export function resolveLauncherPosition(
  saved: LauncherPosition | null,
): LauncherPosition {
  return saved ? clampLauncherPosition(saved) : defaultLauncherPosition();
}

/**
 * Top-left the chat panel should open at so it hangs off the launcher: the
 * panel's bottom-right corner lands on the launcher's, then the whole box is
 * clamped into the viewport. With the button in its default spot this is
 * exactly the old hard-coded `innerWidth - PANEL_WIDTH - 24` — the default
 * opening position is unchanged; only a moved button moves the panel.
 */
export function panelPositionForLauncher(
  saved: LauncherPosition | null,
  panelWidth: number,
  panelHeight: number,
): LauncherPosition {
  const launcher = resolveLauncherPosition(saved);
  return {
    x: clamp(
      launcher.x + LAUNCHER_SIZE - panelWidth,
      0,
      Math.max(0, window.innerWidth - panelWidth),
    ),
    y: clamp(
      launcher.y + LAUNCHER_SIZE - panelHeight,
      0,
      Math.max(0, window.innerHeight - panelHeight),
    ),
  };
}

/**
 * Which side the "Ask Indy" tooltip should hang off. It sits to the LEFT by
 * default (the button lives in the right corner); dragged to the left edge that
 * would run off-screen, so it flips.
 */
export function tooltipOnRight(pos: LauncherPosition): boolean {
  return pos.x + LAUNCHER_SIZE / 2 < window.innerWidth / 2;
}
